import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, ArrowDown, ArrowUp, PiggyBank, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useSoldes, useTresoEvolution } from '@/hooks/queries/useSoldes';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';

const RANGE_OPTIONS = [
  { value: 30, label: '30 jours' },
  { value: 60, label: '60 jours' },
  { value: 90, label: '90 jours' },
];

export function TresoreriePage(): JSX.Element {
  const canView = usePermission('tresorerie.view');
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  const [days, setDays] = useState(30);
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: soldes = [] } = useSoldes(entrepriseId);
  const { data: evolution = [] } = useTresoEvolution(entrepriseId, days);

  const totalDzd = soldes
    .filter((s) => s.devise_code === 'DZD')
    .reduce((acc, s) => acc + s.solde_courant, 0);
  const totalEntrees = evolution.reduce((acc, e) => acc + Number(e.total_entrees_jour), 0);
  const totalSorties = evolution.reduce((acc, e) => acc + Number(e.total_sorties_jour), 0);
  const projete = totalDzd + totalEntrees - totalSorties;

  const decouverts = soldes.filter((s) => s.solde_courant < 0);

  // Aggrège par jour pour le chart
  const chartData = useMemo(() => {
    const byDay = new Map<string, { jour: string; entrees: number; sorties: number }>();
    for (const e of evolution) {
      const key = e.jour;
      const cur = byDay.get(key) ?? { jour: key, entrees: 0, sorties: 0 };
      cur.entrees += Number(e.total_entrees_jour);
      cur.sorties += Number(e.total_sorties_jour);
      byDay.set(key, cur);
    }
    return Array.from(byDay.values()).sort((a, b) => a.jour.localeCompare(b.jour));
  }, [evolution]);

  if (!canView) return <AccessDenied feature="tresorerie.view" />;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('finances.tresorerie.title')}
        subtitle={t('finances.tresorerie.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-4">
        <KpiCard
          icon={<PiggyBank className="h-5 w-5" />}
          label={t('finances.tresorerie.kpiSoldeTotal')}
          value={formatDA(totalDzd)}
          tone={totalDzd >= 0 ? 'primary' : 'destructive'}
        />
        <KpiCard
          icon={<ArrowUp className="h-5 w-5" />}
          label={t('finances.tresorerie.kpiEncaiss')}
          value={formatDA(totalEntrees)}
          tone="success"
        />
        <KpiCard
          icon={<ArrowDown className="h-5 w-5" />}
          label={t('finances.tresorerie.kpiDecaiss')}
          value={formatDA(totalSorties)}
          tone="warning"
        />
        <KpiCard
          icon={<TrendingUp className="h-5 w-5" />}
          label={t('finances.tresorerie.kpiProjete')}
          value={formatDA(projete)}
          tone={projete >= 0 ? 'primary' : 'destructive'}
        />
      </div>

      {decouverts.length > 0 && (
        <div className="space-y-2 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-destructive">
            <AlertTriangle className="h-4 w-4" />
            {t('finances.tresorerie.alertesDecouvert')}
          </div>
          <ul className="space-y-1 pl-6 text-xs">
            {decouverts.map((d) => (
              <li key={d.compte_id} className="text-destructive">
                <span className="font-medium">{d.intitule}</span> :{' '}
                <span className="num">{formatDA(d.solde_courant)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="text-sm font-semibold">{t('finances.tresorerie.evolution')}</h3>
        <div className="mt-3 h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="jour"
                tick={{ fontSize: 11 }}
                tickFormatter={(v: string) => formatDateDZ(v).slice(0, 5)}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) =>
                  new Intl.NumberFormat('fr-DZ', {
                    notation: 'compact',
                    maximumFractionDigits: 1,
                  }).format(v)
                }
              />
              <Tooltip
                formatter={(value: number) => formatDA(value)}
                labelFormatter={(v) => formatDateDZ(String(v))}
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="entrees"
                name="Entrées"
                fill="hsl(var(--success))"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="sorties"
                name="Sorties"
                fill="hsl(var(--warning))"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <header className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold">Soldes par compte</h3>
        </header>
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Compte</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Devise</th>
              <th className="px-3 py-2 text-right">Solde initial</th>
              <th className="px-3 py-2 text-right">Total entrées</th>
              <th className="px-3 py-2 text-right">Total sorties</th>
              <th className="px-3 py-2 text-right">Solde courant</th>
            </tr>
          </thead>
          <tbody>
            {soldes.map((s) => (
              <tr
                key={s.compte_id}
                className="border-b border-border last:border-0 hover:bg-muted/20"
              >
                <td className="px-3 py-2 font-medium">{s.intitule}</td>
                <td className="px-3 py-2">
                  <Badge variant={s.compte_type === 'caisse' ? 'outline' : 'default'}>
                    {s.compte_type}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-xs">{s.devise_code}</td>
                <td className="num px-3 py-2 text-right text-xs">{formatDA(s.solde_initial)}</td>
                <td className="num px-3 py-2 text-right text-success">
                  {formatDA(s.total_entrees)}
                </td>
                <td className="num px-3 py-2 text-right text-warning">
                  {formatDA(s.total_sorties)}
                </td>
                <td
                  className={cn(
                    'num px-3 py-2 text-right font-semibold',
                    s.solde_courant < 0 && 'text-destructive',
                  )}
                >
                  {formatDA(s.solde_courant)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: 'primary' | 'success' | 'warning' | 'destructive';
}): JSX.Element {
  const toneCls = {
    primary: 'text-primary bg-primary/10',
    success: 'text-success bg-success/10',
    warning: 'text-warning bg-warning/10',
    destructive: 'text-destructive bg-destructive/10',
  } as const;
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        <div className={cn('rounded-md p-1.5', toneCls[tone])}>{icon}</div>
      </div>
      <div
        className={cn(
          'num mt-2 text-2xl font-semibold',
          tone === 'destructive' && 'text-destructive',
        )}
      >
        {value}
      </div>
    </div>
  );
}
